import React, { useEffect, useState } from 'react';
import {
  X, Loader2, Clock, AlertTriangle, History, Plus, Trash2, ChevronRight,
} from 'lucide-react';
import {
  getScheduleVersionHistory,
  createScheduleVersion,
  createScheduleVersionBounded,
  deleteScheduleVersion,
  getWorkHoursAtDate,
  saveWeekSchedule,
  DAYS_OF_WEEK,
  dayOfWeekMeta,
} from '../services/api';
import { useToast } from './Toast';
import { FEEDBACK } from '../services/feedbackMessages';

// Histórico de HORARIOS de un trabajador.
//
// Si recibe `onChanged` (callback de recarga) se considera modo EDICIÓN:
// muestra el botón "Añadir horario", el editor de los 7 días y permite borrar
// tarjetas. Sin él, queda en SOLO LECTURA. El control de permisos lo hace
// WorkerDetailPage pasando o no la prop.
//
// Modos del formulario "Añadir horario":
//   · "Desde una fecha en adelante" (solo desde)
//     → /api/scheduleVersion/create-unbounded — auto-cierra el activo.
//     El flujo es idéntico al del editor de horarios del WorkerModal:
//     creamos la versión y luego guardamos los 7 días — el backend asocia
//     los upserts a la versión vigente (la que acabamos de crear).
//
//   · "Periodo cerrado" (desde + hasta)
//     → /api/scheduleVersion/create-bounded — para tramos históricos.
//     Aquí necesitamos que el backend acepte `scheduleVersionId` en el body
//     de POST /api/workhours/ para asociar los días a la versión histórica
//     concreta (no a la activa de hoy). Mientras el compi no lo despliegue,
//     pasamos el campo igual: cuando esté, funcionará sin tocar nada más.

const trimSecs = (v) => v ? String(v).slice(0, 5) : '';
const HMS_TO_MIN = (hms) => {
  if (!hms) return null;
  const [h, m] = String(hms).slice(0, 5).split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
};

const weeklyMinutesForVersion = (version) => {
  const slots = Array.isArray(version?.assignSlots) ? version.assignSlots : [];
  let total = 0;
  for (const s of slots) {
    const in1 = HMS_TO_MIN(s.in1), out1 = HMS_TO_MIN(s.out1);
    const in2 = HMS_TO_MIN(s.in2), out2 = HMS_TO_MIN(s.out2);
    if (in1 != null && out1 != null && out1 > in1) total += out1 - in1;
    if (in2 != null && out2 != null && out2 > in2) total += out2 - in2;
  }
  return total;
};

const fmtHoursMinutes = (mins) => {
  if (!mins) return '0h';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${String(m).padStart(2, '0')}m`;
};

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString('es-ES', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return iso; }
};

const todayIso = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

// Plantilla por defecto del editor: lun-vie 9-17, sábado y domingo descanso.
// Misma convención que el editor del WorkerModal para que sea familiar.
const defaultWeek = () =>
  DAYS_OF_WEEK.map(({ value }) => ({
    dayOfWeek: value,
    isOff: value === 0 || value === 6,
    in1: '09:00', out1: '17:00',
    in2: '', out2: '',
  }));

// Mismo helper que el WorkerModal: si solo hay turno de tarde, lo subimos al
// primer slot. Si solo hay mañana, dejamos in2/out2 a null. El backend
// representa cada día con dos slots (mañana/tarde) y null en los que faltan.
const toBackendDay = (day) => {
  if (day.isOff) return { dayOfWeek: day.dayOfWeek, isOff: true };
  const has1 = !!day.in1 && !!day.out1;
  const has2 = !!day.in2 && !!day.out2;
  if (has1 && has2) {
    return { dayOfWeek: day.dayOfWeek, isOff: false, in1: day.in1, out1: day.out1, in2: day.in2, out2: day.out2 };
  }
  if (has1) {
    return { dayOfWeek: day.dayOfWeek, isOff: false, in1: day.in1, out1: day.out1, in2: null, out2: null };
  }
  return { dayOfWeek: day.dayOfWeek, isOff: false, in1: day.in2, out1: day.out2, in2: null, out2: null };
};

// Editor inline de los 7 días. Cada fila: nombre del día, checkbox descanso,
// dos pares de hora (mañana / tarde). El admin puede dejar el segundo par
// vacío si la jornada es turno único.
const WeekEditor = ({ week, onChange, disabled }) => {
  const setField = (dow, field, value) => {
    onChange(week.map((d) => Number(d.dayOfWeek) === Number(dow) ? { ...d, [field]: value } : d));
  };
  return (
    <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-ink-muted">
          <tr>
            <th className="px-2 py-1.5 text-left font-semibold uppercase tracking-wider text-[10px]">Día</th>
            <th className="px-1 py-1.5 text-center font-semibold uppercase tracking-wider text-[10px]" colSpan={2}>Mañana</th>
            <th className="px-1 py-1.5 text-center font-semibold uppercase tracking-wider text-[10px]" colSpan={2}>Tarde</th>
            <th className="px-2 py-1.5 text-center font-semibold uppercase tracking-wider text-[10px]">Descanso</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {week.map((d) => {
            const meta = dayOfWeekMeta(d.dayOfWeek);
            return (
              <tr key={d.dayOfWeek} className={d.isOff ? 'bg-slate-50/60' : ''}>
                <td className="px-2 py-1.5 font-semibold text-ink capitalize">{meta.label}</td>
                <td className="px-1 py-1">
                  <input
                    type="time"
                    value={d.isOff ? '' : (d.in1 || '')}
                    onChange={(e) => setField(d.dayOfWeek, 'in1', e.target.value)}
                    disabled={disabled || d.isOff}
                    className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs disabled:bg-slate-100 disabled:text-ink-muted/40 tabular-nums"
                  />
                </td>
                <td className="px-1 py-1">
                  <input
                    type="time"
                    value={d.isOff ? '' : (d.out1 || '')}
                    onChange={(e) => setField(d.dayOfWeek, 'out1', e.target.value)}
                    disabled={disabled || d.isOff}
                    className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs disabled:bg-slate-100 disabled:text-ink-muted/40 tabular-nums"
                  />
                </td>
                <td className="px-1 py-1">
                  <input
                    type="time"
                    value={d.isOff ? '' : (d.in2 || '')}
                    onChange={(e) => setField(d.dayOfWeek, 'in2', e.target.value)}
                    disabled={disabled || d.isOff}
                    placeholder="—"
                    className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs disabled:bg-slate-100 disabled:text-ink-muted/40 tabular-nums"
                  />
                </td>
                <td className="px-1 py-1">
                  <input
                    type="time"
                    value={d.isOff ? '' : (d.out2 || '')}
                    onChange={(e) => setField(d.dayOfWeek, 'out2', e.target.value)}
                    disabled={disabled || d.isOff}
                    placeholder="—"
                    className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs disabled:bg-slate-100 disabled:text-ink-muted/40 tabular-nums"
                  />
                </td>
                <td className="px-2 py-1 text-center">
                  <input
                    type="checkbox"
                    checked={!!d.isOff}
                    onChange={(e) => setField(d.dayOfWeek, 'isOff', e.target.checked)}
                    disabled={disabled}
                    title="Marcar como día de descanso (sin horario)"
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const WorkerScheduleHistoryModal = ({ open, worker, onClose, onChanged }) => {
  const canEdit = typeof onChanged === 'function';

  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('bounded');
  const [formValidFrom, setFormValidFrom] = useState('');
  const [formValidTo, setFormValidTo] = useState('');
  const [formWeek, setFormWeek] = useState(defaultWeek());
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const toast = useToast();

  const reload = async () => {
    if (!worker?.id) return;
    setLoading(true);
    setError(null);
    try {
      const list = await getScheduleVersionHistory(worker.id);
      const arr = Array.isArray(list) ? list : [];
      // Workaround del backend: GET /api/scheduleVersion/history/:id NO
      // devuelve los días de cada horario (lo prometía la doc pero falla).
      // Resolvemos los días vía /api/workhours/:workerId/date/:date con una
      // fecha dentro del rango del horario (validFrom funciona). Cuando el
      // backend joinee los días, este enriquecimiento se vuelve no-op.
      const enriched = await Promise.all(arr.map(async (v) => {
        if (Array.isArray(v.assignSlots)) return v;
        try {
          const sample = v.validFrom || new Date().toISOString().slice(0, 10);
          const res = await getWorkHoursAtDate(worker.id, sample);
          const slots = Array.isArray(res?.workhours) ? res.workhours : [];
          return { ...v, assignSlots: slots };
        } catch {
          return { ...v, assignSlots: [] };
        }
      }));
      setVersions(enriched);
    } catch (err) {
      setError(err);
      setVersions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    reload();
    setFormOpen(false);
    setFormMode('bounded');
    setFormValidFrom('');
    setFormValidTo('');
    setFormWeek(defaultWeek());
    setFormError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, worker?.id]);

  const handleDelete = async (v) => {
    const range = `${fmtDate(v.validFrom)} → ${v.validTo ? fmtDate(v.validTo) : 'hoy'}`;
    if (!window.confirm(`¿Eliminar este horario (${range})?\n\nNo se puede deshacer.`)) return;
    setDeletingId(v.id);
    try {
      await deleteScheduleVersion(v.id);
      toast.success(FEEDBACK.scheduleVersionDeleted);
      await reload();
      if (onChanged) onChanged();
    } catch (err) {
      // Toast y no `setError`: ese estado sustituye el listado entero por el
      // banner de error de carga.
      toast.error(err, 'No se pudo eliminar el horario.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!formValidFrom) { setFormError('Indica la fecha de inicio.'); return; }
    if (formMode === 'bounded') {
      if (!formValidTo) { setFormError('En periodo cerrado debes indicar la fecha fin.'); return; }
      if (formValidTo < formValidFrom) { setFormError('La fecha fin no puede ser anterior al inicio.'); return; }
    }
    // Validar que al menos un día tenga horario configurado (o todos descanso
    // es válido si el admin lo decide, pero avisamos).
    const anyConfigured = formWeek.some((d) => !d.isOff && d.in1 && d.out1);
    if (!anyConfigured) {
      const ok = window.confirm('No has configurado ningún día con horario. ¿Quieres guardar igualmente con todos los días como descanso?');
      if (!ok) return;
    }
    // Validación básica de horas: si un día está activo, debe tener in1+out1
    // y out1 > in1. Si tiene in2/out2, mismo criterio.
    for (const d of formWeek) {
      if (d.isOff) continue;
      if (!d.in1 || !d.out1) { setFormError(`Falta hora de mañana en ${dayOfWeekMeta(d.dayOfWeek).label}.`); return; }
      if (d.out1 <= d.in1) { setFormError(`En ${dayOfWeekMeta(d.dayOfWeek).label}, la hora de salida de mañana debe ser posterior a la de entrada.`); return; }
      const has2 = d.in2 || d.out2;
      if (has2) {
        if (!d.in2 || !d.out2) { setFormError(`En ${dayOfWeekMeta(d.dayOfWeek).label}, si rellenas tarde debes poner entrada y salida.`); return; }
        if (d.out2 <= d.in2) { setFormError(`En ${dayOfWeekMeta(d.dayOfWeek).label}, la hora de salida de tarde debe ser posterior a la de entrada.`); return; }
      }
    }

    setSubmitting(true);
    let createdVersionId = null;
    try {
      // 1) Crear el horario (versión) en backend.
      let created;
      if (formMode === 'bounded') {
        created = await createScheduleVersionBounded({
          workerId: worker.id,
          validFrom: formValidFrom,
          validTo: formValidTo,
        });
      } else {
        created = await createScheduleVersion({
          workerId: worker.id,
          validFrom: formValidFrom,
        });
      }
      createdVersionId = created?.id ?? null;

      // 2) Crear los 7 días asociados a esa versión.
      //    Pasamos el scheduleVersionId del horario que acabamos de crear
      //    para que el backend asocie cada día a la versión correcta (la
      //    nueva), no a la activa de hoy si es histórica.
      await saveWeekSchedule(worker.id, formWeek.map(toBackendDay), createdVersionId);
      toast.success(FEEDBACK.scheduleVersionCreated({
        validFrom: formValidFrom,
        validTo: formMode === 'bounded' ? formValidTo : null,
      }));

      setFormOpen(false);
      setFormValidFrom('');
      setFormValidTo('');
      setFormWeek(defaultWeek());
      await reload();
      if (onChanged) onChanged();
    } catch (err) {
      // Rollback: si conseguimos crear la versión pero falló alguno de los
      // días, borramos la versión recién creada para no dejarla huérfana
      // (vacía o con días incompletos). Si el rollback también falla, se
      // queda como está y se informa al admin.
      if (createdVersionId) {
        try {
          await deleteScheduleVersion(createdVersionId);
          setFormError(`${err?.message || 'Error al crear el horario.'} (Cambio revertido — el horario no se ha guardado.)`);
        } catch (rollbackErr) {
          setFormError(`${err?.message || 'Error al crear el horario.'} Además falló el rollback (${rollbackErr?.message || 'error desconocido'}); elimina el horario manualmente desde el listado.`);
        }
      } else {
        setFormError(err?.message || 'No se pudo crear el horario.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  // Activa arriba, después por validFrom descendente.
  const sorted = [...versions].sort((a, b) => {
    if (!a.validTo && b.validTo) return -1;
    if (a.validTo && !b.validTo) return 1;
    return String(b.validFrom || '').localeCompare(String(a.validFrom || ''));
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold inline-flex items-center gap-1.5">
              <History size={12} /> Histórico
            </div>
            <h2 className="font-display text-lg font-bold text-ink truncate">
              Horarios de {worker?.name || `Trabajador #${worker?.id}`}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-ink-muted hover:text-ink p-1 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {canEdit && (
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs text-ink-muted">
                {loading ? 'Cargando…' : sorted.length === 0 ? 'Sin horarios registrados.' : `${sorted.length} horario${sorted.length === 1 ? '' : 's'}`}
              </div>
              <button
                onClick={() => setFormOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-primary hover:bg-primary-dark transition-colors"
              >
                <Plus size={12} /> {formOpen ? 'Cancelar' : 'Añadir horario'}
              </button>
            </div>
          )}

          {canEdit && formOpen && (
            <form onSubmit={handleSubmit} className="rounded-lg border-2 border-primary/30 bg-primary-soft/30 p-3 space-y-3">
              <div className="text-xs font-semibold text-ink">Nuevo horario</div>

              <div>
                <label className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold block mb-1">Tipo</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setFormMode('bounded')}
                    disabled={submitting}
                    className={`text-left text-xs rounded-md border-2 px-2.5 py-2 transition-colors ${
                      formMode === 'bounded'
                        ? 'border-primary bg-white text-ink'
                        : 'border-slate-200 bg-white/50 text-ink-muted hover:border-slate-300'
                    }`}
                  >
                    <div className="font-semibold">Periodo cerrado</div>
                    <div className="text-[10px] mt-0.5 leading-tight">Con fecha de fin. Útil para horarios pasados o futuros con duración conocida.</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormMode('open')}
                    disabled={submitting}
                    className={`text-left text-xs rounded-md border-2 px-2.5 py-2 transition-colors ${
                      formMode === 'open'
                        ? 'border-primary bg-white text-ink'
                        : 'border-slate-200 bg-white/50 text-ink-muted hover:border-slate-300'
                    }`}
                  >
                    <div className="font-semibold">Desde una fecha en adelante</div>
                    <div className="text-[10px] mt-0.5 leading-tight">Sin fecha fin. Cierra automáticamente el horario vigente.</div>
                  </button>
                </div>
              </div>

              <div className={`grid gap-2 ${formMode === 'bounded' ? 'grid-cols-2' : 'grid-cols-1'}`}>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold block mb-1">Desde</label>
                  <input
                    type="date"
                    value={formValidFrom}
                    onChange={(e) => setFormValidFrom(e.target.value)}
                    max={formMode === 'open' ? todayIso() : undefined}
                    className="w-full text-sm px-2.5 py-1.5 rounded-md border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 tabular-nums"
                    disabled={submitting}
                  />
                </div>
                {formMode === 'bounded' && (
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold block mb-1">Hasta</label>
                    <input
                      type="date"
                      value={formValidTo}
                      onChange={(e) => setFormValidTo(e.target.value)}
                      min={formValidFrom || undefined}
                      className="w-full text-sm px-2.5 py-1.5 rounded-md border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 tabular-nums"
                      disabled={submitting}
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold block mb-1">
                  Horario de cada día
                </label>
                <WeekEditor week={formWeek} onChange={setFormWeek} disabled={submitting} />
                <div className="text-[10px] text-ink-muted mt-1">
                  Si la jornada es de turno único, deja vacía la columna de tarde. Marca "Descanso" en los días que no trabaja.
                </div>
              </div>

              {formError && (
                <div className="text-xs text-danger font-semibold inline-flex items-center gap-1.5">
                  <AlertTriangle size={12} /> {formError}
                </div>
              )}

              <div className="flex items-center gap-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-primary hover:bg-primary-dark transition-colors disabled:opacity-60"
                >
                  {submitting ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                  Guardar horario
                </button>
                <button
                  type="button"
                  onClick={() => setFormOpen(false)}
                  disabled={submitting}
                  className="text-xs text-ink-muted hover:text-ink px-2 py-1.5 rounded-md transition-colors disabled:opacity-60"
                >
                  Cancelar
                </button>
              </div>
            </form>
          )}

          {loading && (
            <div className="py-12 flex items-center justify-center gap-2 text-ink-muted">
              <Loader2 size={18} className="animate-spin text-primary" />
              <span className="text-sm">Cargando histórico…</span>
            </div>
          )}

          {error && !loading && (
            <div className="border border-danger/30 bg-danger/5 rounded-xl p-4 flex items-start gap-3">
              <AlertTriangle size={16} className="text-danger shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-semibold text-ink">No se pudo cargar el histórico</div>
                <div className="text-ink-muted mt-0.5">{error.message || 'Error desconocido.'}</div>
              </div>
            </div>
          )}

          {!loading && !error && sorted.length === 0 && !formOpen && (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-100 mb-3">
                <Clock size={20} className="text-ink-muted/70" />
              </div>
              <div className="font-display text-sm font-bold text-ink mb-1">
                Sin horarios registrados
              </div>
              <div className="text-xs text-ink-muted max-w-sm mx-auto">
                Este trabajador no tiene horarios registrados todavía. Mientras tanto,
                el sistema usa su horario actual para todo el histórico de fichajes.
              </div>
            </div>
          )}

          {!loading && !error && sorted.length > 0 && (
            <ul className="space-y-3">
              {sorted.map((v) => {
                const active = !v.validTo;
                const weeklyMins = weeklyMinutesForVersion(v);
                const slots = Array.isArray(v.assignSlots) ? v.assignSlots : [];
                return (
                  <li
                    key={v.id}
                    className={`rounded-xl border-2 overflow-hidden ${
                      active ? 'border-primary/30 bg-primary-soft/20' : 'border-slate-200 bg-white'
                    }`}
                  >
                    <div className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap border-b border-slate-200 bg-white/60">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-display text-sm font-bold text-ink tabular-nums inline-flex items-center gap-1">
                            {fmtDate(v.validFrom) || '—'}
                            <ChevronRight size={12} className="text-ink-muted" />
                            {v.validTo ? fmtDate(v.validTo) : <span className="text-ink-muted italic">hoy</span>}
                          </span>
                          {active && (
                            <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-primary text-white">
                              Vigente
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-ink-muted mt-0.5">
                          {slots.length} {slots.length === 1 ? 'día configurado' : 'días configurados'}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold">
                          Total semanal
                        </div>
                        <div className="font-display text-lg font-bold text-primary-darker tabular-nums">
                          {fmtHoursMinutes(weeklyMins)}
                        </div>
                      </div>
                      {canEdit && (
                        <button
                          onClick={() => handleDelete(v)}
                          disabled={deletingId === v.id}
                          className="text-ink-muted hover:text-danger p-1.5 rounded-md hover:bg-danger/5 transition-colors disabled:opacity-40 shrink-0"
                          title="Eliminar este horario (no se puede deshacer)"
                        >
                          {deletingId === v.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                        </button>
                      )}
                    </div>

                    {slots.length === 0 ? (
                      <div className="py-4 text-center text-xs text-ink-muted italic">
                        Este horario no tiene días configurados.
                      </div>
                    ) : (
                      <ul className="divide-y divide-slate-100">
                        {DAYS_OF_WEEK.map(({ value }) => {
                          const slot = slots.find((s) => Number(s.dayOfWeek) === value);
                          const morning = slot && trimSecs(slot.in1) && trimSecs(slot.out1)
                            ? `${trimSecs(slot.in1)} – ${trimSecs(slot.out1)}` : null;
                          const evening = slot && trimSecs(slot.in2) && trimSecs(slot.out2)
                            ? `${trimSecs(slot.in2)} – ${trimSecs(slot.out2)}` : null;
                          const isOff = !morning && !evening;
                          return (
                            <li
                              key={value}
                              className={`grid grid-cols-[100px_1fr] gap-3 px-4 py-2 text-xs ${isOff ? 'bg-slate-50/60' : ''}`}
                            >
                              <div className="font-semibold text-ink capitalize">
                                {dayOfWeekMeta(value).label}
                              </div>
                              <div className="text-ink-muted tabular-nums">
                                {isOff ? (
                                  <span className="italic text-ink-muted/60">Descanso</span>
                                ) : (
                                  <span className="text-ink font-mono">
                                    {morning || <span className="text-ink-muted/60">—</span>}
                                    {' · '}
                                    {evening || <span className="text-ink-muted/60">—</span>}
                                  </span>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-between gap-2 shrink-0">
          <div className="text-[11px] text-ink-muted">
            {canEdit
              ? 'Para editar un horario existente: bórralo y crea uno nuevo con las mismas fechas.'
              : 'Vista de solo lectura. Pide acceso de gestión para editar.'}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-ink-muted hover:bg-slate-100 transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkerScheduleHistoryModal;
